import React, { useCallback, useState } from 'react';

import { Menu, Popover } from '@arco-design/web-react';
import { ToolItem } from '../ToolItem';
import { IconFont, useBlock, useFocusIdx } from '@bay-email/editor';
import { get } from 'lodash';
import { useTiptapEditor } from '../Tools/TiptapEditorContext';
import { t } from '@bay-email/core';
import { getSelectionTextStyle } from '../Tools/selectionTextStyle';
import styleText from '../../styles/ToolsPopover.css?inline';

const list = [
	{
		value: '12',
		label: '12px',
	},
	{
		value: '13',
		label: '13px',
	},
	{
		value: '16',
		label: '16px',
	},
	{
		value: '18',
		label: '18px',
	},
	{
		value: '24',
		label: '24px',
	},
	{
		value: '32',
		label: '32px',
	},
	{
		value: '48',
		label: '48px',
	},
	{
		value: '56',
		label: '56px',
	},
	{
		value: '64',
		label: '64px',
	},
	{
		value: '72',
		label: '72px',
	},
	{
		value: '96',
		label: '96px',
	},
];

export interface FontSizeProps {
	execCommand: (cmd: string, value: any) => void;
	getPopupContainer: () => HTMLElement;
	onBeforeInteract?: () => void;
}

export function FontSize(props: FontSizeProps) {
	const { execCommand, onBeforeInteract } = props;
	const editor = useTiptapEditor();
	const { focusIdx } = useFocusIdx();
	const { values } = useBlock();
	// What the selection renders in (inline size, else block default); '' when mixed.
	const currentSize = editor
		? getSelectionTextStyle(
				editor,
				'fontSize',
				focusIdx ? String(get(values, `${focusIdx}.attributes.font-size`) || '') : '',
			)
		: '';
	const [visible, setVisible] = useState(false);
	const [customSize, setCustomSize] = useState('');

	const onChange = useCallback(
		(val: string) => {
			onBeforeInteract?.();
			execCommand('fontSizePx', val);
			setVisible(false);
		},
		[execCommand, onBeforeInteract],
	);

	const onApplyCustomSize = useCallback(() => {
		const pxValue = parseInt(customSize, 10);
		if (Number.isNaN(pxValue)) return;

		const clampedSize = Math.max(8, Math.min(pxValue, 300));
		onBeforeInteract?.();
		execCommand('fontSizePx', String(clampedSize));
		setVisible(false);
		setCustomSize('');
	}, [customSize, execCommand, onBeforeInteract]);

	const onCustomKeyDown = useCallback(
		(evt: React.KeyboardEvent<HTMLInputElement>) => {
			if (evt.key === 'Enter') {
				evt.preventDefault();
				onApplyCustomSize();
			}
		},
		[onApplyCustomSize],
	);

	const onVisibleChange = useCallback(
		(v: boolean) => {
			if (v) {
				onBeforeInteract?.();
			}
			setCustomSize(v ? currentSize : '');
			setVisible(v);
		},
		[currentSize, onBeforeInteract],
	);

	return (
		<Popover
			trigger='click'
			color='#fff'
			position='bl'
			className='bay-email-extensions-Tools-Popover'
			popupVisible={visible}
			onVisibleChange={onVisibleChange}
			content={
				<>
					<style>{styleText}</style>
					<div
						onMouseDownCapture={() => onBeforeInteract?.()}
						onKeyDownCapture={() => onBeforeInteract?.()}
						style={{
							maxWidth: 150,
							maxHeight: 350,
							overflowY: 'auto',
							overflowX: 'hidden',
						}}
					>
						<div
							style={{
								display: 'flex',
								gap: 6,
								padding: '8px 8px 6px',
							}}
						>
							<input
								type='number'
								min={8}
								max={300}
								placeholder={t('Size')}
								value={customSize}
								onChange={evt => setCustomSize(evt.target.value)}
								onKeyDown={onCustomKeyDown}
								style={{
									width: '100%',
									height: 28,
									padding: '0 8px',
									border: '1px solid #d0d5dd',
									borderRadius: 4,
									fontSize: 12,
								}}
							/>
							<button
								type='button'
								onClick={onApplyCustomSize}
								style={{
									height: 28,
									padding: '0 8px',
									border: '1px solid #d0d5dd',
									borderRadius: 4,
									background: '#fff',
									cursor: 'pointer',
									fontSize: 12,
								}}
							>
								px
							</button>
						</div>
						<Menu
							onClickMenuItem={onChange}
							selectedKeys={currentSize ? [currentSize] : []}
							style={{ border: 'none', padding: 0 }}
						>
							{list.map(item => (
								<Menu.Item
									style={{ lineHeight: '30px', height: 30 }}
									key={item.value}
								>
									{item.label}
								</Menu.Item>
							))}
						</Menu>
					</div>
				</>
			}
			getPopupContainer={props.getPopupContainer}
		>
			<ToolItem
				title={t('Font Size')}
				style={currentSize ? { width: 'auto', padding: '0 6px', gap: 4 } : undefined}
				icon={
					<>
						<IconFont iconName='icon-font-color' />
						{currentSize && <span style={{ fontSize: 12 }}>{`${currentSize}px`}</span>}
					</>
				}
			/>
		</Popover>
	);
}
